import { useState } from 'react'
import { exportIntelligencePDF } from '../../../shared/utils/intelligencePdfExport'
import { supabase } from '../../../shared/lib/supabase'

function fmt(v) { return v || 'N/A' }

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-gray-100">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  )
}

function Badge({ children, color = 'gray' }) {
  const colors = {
    green: 'bg-green-100 text-green-700', red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700', blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700', gray: 'bg-gray-100 text-gray-600'
  }
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors[color] || colors.gray}`}>{children}</span>
}

function Grade({ grade, size = 'lg' }) {
  const colors = { A: 'text-green-600 bg-green-50 border-green-200', B: 'text-green-500 bg-green-50 border-green-200', C: 'text-amber-500 bg-amber-50 border-amber-200', D: 'text-orange-500 bg-orange-50 border-orange-200', F: 'text-red-500 bg-red-50 border-red-200' }
  const s = size === 'lg' ? 56 : 44
  const fs = size === 'lg' ? 28 : 20
  return <div style={{ width: s, height: s, fontSize: fs, lineHeight: 1, borderRadius: 12, flexShrink: 0 }} className={`border flex items-center justify-center font-bold ${colors[grade] || 'text-gray-400 bg-gray-50 border-gray-200'}`}>{grade}</div>
}

function StatusDot({ ok }) {
  return <div className={`w-2 h-2 rounded-full flex-shrink-0 ${ok ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
}

export default function ClientEnrichment() {
  const [companyName, setCompanyName] = useState('')
  const [domain, setDomain] = useState('')
  const [additionalContext, setAdditionalContext] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('business')

  const handleEnrich = async () => {
    if (!companyName.trim() || !domain.trim()) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      // Production requires auth (Cloudflare function enforces it). Local dev is open.
      if (!import.meta.env.DEV && !session?.access_token) {
        throw new Error('You must be signed in to run client intelligence. Please log in first.')
      }
      const headers = { 'Content-Type': 'application/json' }
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`
      const res = await fetch('/api/demo/enrich', {
        method: 'POST',
        headers,
        body: JSON.stringify({ companyName: companyName.trim(), domain: domain.trim(), additionalContext: additionalContext.trim() || undefined })
      })
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Enrichment failed') }
      setResult(await res.json())
      setActiveTab('business')
    } catch (err) { setError(err.message) } finally { setLoading(false) }
  }

  const e = result?.enrichment
  const dns = result?.dns
  const ssl = result?.ssl
  const sslLabs = result?.sslLabs
  const observatory = result?.observatory
  const whois = result?.whois
  const [exporting, setExporting] = useState(false)

  const exportPDF = async () => {
    if (!e) return
    setExporting(true)
    try {
      exportIntelligencePDF({
        companyName,
        enrichment: e,
        dns, ssl, sslLabs, observatory, whois,
        ctSubdomains: result?.ctSubdomains,
        shodan: result?.shodan
      })
    } catch (err) {
      console.error('[PDF Export] Error:', err)
    } finally {
      setExporting(false)
    }
  }

  const tabs = [
    { id: 'business', label: 'Business Context' },
    { id: 'security', label: 'Security Posture' },
    { id: 'engagement', label: 'Engagement Intel' }
  ]

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f0f4f8' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #012438, #213C4E)' }} className="px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo.png" alt="Vantage" className="w-8 h-8 rounded-lg" />
            <div>
              <h1 className="text-lg font-bold text-white">Client Intelligence</h1>
              <p className="text-xs" style={{ color: '#7E8F9C' }}>AI-Powered Engagement Enrichment</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#8AD0D6' }}>Company Name</label>
              <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Acme Financial Services"
                className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 text-sm focus:ring-2 focus:ring-[#8AD0D6] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#8AD0D6' }}>Primary Domain</label>
              <input type="text" value={domain} onChange={e => setDomain(e.target.value)} placeholder="acmefinancial.com"
                className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 text-sm focus:ring-2 focus:ring-[#8AD0D6] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#8AD0D6' }}>Additional Context <span className="font-normal text-gray-400">(optional)</span></label>
              <input type="text" value={additionalContext} onChange={e => setAdditionalContext(e.target.value)} placeholder="e.g., 200 employees, processes credit cards"
                className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 text-sm focus:ring-2 focus:ring-[#8AD0D6] focus:border-transparent" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button onClick={handleEnrich} disabled={loading || !companyName.trim() || !domain.trim()}
              className="px-6 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: '#8AD0D6', color: '#012438' }}>
              {loading ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#012438]" />Analyzing...</>) : (
                <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" /></svg>Enrich Client</>
              )}
            </button>
            {loading && <span className="text-xs" style={{ color: '#7E8F9C' }}>This typically takes 15-20 seconds</span>}
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-5xl mx-auto px-6 mt-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        </div>
      )}

      {e && (
        <div className="max-w-5xl mx-auto px-6 py-6">
          {/* Company header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{companyName}</h2>
              <p className="text-sm text-gray-500">{e.companyProfile?.industry}{e.companyProfile?.subIndustry ? ` / ${e.companyProfile.subIndustry}` : ''} {e.companyProfile?.headquarters ? `| ${e.companyProfile.headquarters}` : ''}</p>
            </div>
            <div className="flex items-center gap-3">
              {e.securityPosture?.overallGrade && <Grade grade={e.securityPosture.overallGrade} />}
              {e.confidence && <Badge color={e.confidence.overall === 'high' ? 'green' : e.confidence.overall === 'medium' ? 'amber' : 'red'}>{e.confidence.overall} confidence</Badge>}
              <button
                onClick={exportPDF}
                disabled={exporting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {exporting ? (
                  <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600" />Exporting...</>
                ) : (
                  <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>Export PDF</>
                )}
              </button>
            </div>
          </div>

          {/* Tab bar */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex gap-6">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === tab.id ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                  {tab.label}
                  {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#012438] rounded-full" />}
                </button>
              ))}
            </nav>
          </div>

          {/* ── Business Context Tab ── */}
          {activeTab === 'business' && (
            <div className="space-y-5">
              <Section title="Company Profile">
                <p className="text-sm text-gray-600 mb-4">{e.companyProfile?.description}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Industry', value: e.companyProfile?.industry },
                    { label: 'Sub-Industry', value: e.companyProfile?.subIndustry },
                    { label: 'Business Model', value: e.companyProfile?.businessModel },
                    { label: 'Est. Size', value: e.companyProfile?.estimatedSize },
                    { label: 'Est. Revenue', value: e.companyProfile?.estimatedRevenue },
                    { label: 'Headquarters', value: e.companyProfile?.headquarters },
                    { label: 'Years in Operation', value: e.companyProfile?.yearsInOperation },
                    { label: 'Domain Age', value: whois?.domainAge != null ? `${whois.domainAge} years` : 'Unknown' },
                  ].map(item => (
                    <div key={item.label}>
                      <p className="text-xs text-gray-400">{item.label}</p>
                      <p className="text-sm font-medium text-gray-900">{fmt(item.value)}</p>
                    </div>
                  ))}
                </div>
              </Section>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Section title="Technology Profile">
                  <div className="space-y-2.5">
                    {[
                      { label: 'Email Platform', value: e.technologyProfile?.emailPlatform },
                      { label: 'Cloud Platform', value: e.technologyProfile?.cloudPlatform },
                      { label: 'Identity Platform', value: e.technologyProfile?.identityPlatform },
                      { label: 'DNS Provider', value: e.technologyProfile?.dnsProvider || dns?.dnsProvider },
                      { label: 'Hosting', value: e.technologyProfile?.hostingProvider },
                      { label: 'Website', value: e.technologyProfile?.websiteTechnology },
                    ].map(item => (
                      <div key={item.label} className="grid grid-cols-[110px_1fr] gap-2 text-sm">
                        <span className="text-gray-500 flex-shrink-0">{item.label}</span>
                        <span className="font-medium text-gray-900">{fmt(item.value)}</span>
                      </div>
                    ))}
                    {(e.technologyProfile?.detectedServices || dns?.detectedServices)?.length > 0 && (
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">Detected Services</p>
                        <div className="flex flex-wrap gap-1">
                          {[...new Set([...(e.technologyProfile?.detectedServices || []), ...(dns?.detectedServices || [])])].map((s, i) => <Badge key={i}>{s}</Badge>)}
                        </div>
                      </div>
                    )}
                  </div>
                </Section>

                <Section title="Regulatory Profile">
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(e.regulatoryProfile?.applicableFrameworks || []).map((fw, i) => <Badge key={i} color="blue">{fw}</Badge>)}
                    </div>
                    <div className="space-y-1.5">
                      {[
                        { label: 'Publicly Traded', value: e.regulatoryProfile?.publiclyTraded },
                        { label: 'Government Contractor', value: e.regulatoryProfile?.governmentContractor },
                        { label: 'Processes Payments', value: e.regulatoryProfile?.processesPayments },
                        { label: 'Handles Health Data', value: e.regulatoryProfile?.handlesHealthData },
                        { label: 'International Operations', value: e.regulatoryProfile?.operatesInternationally },
                      ].filter(item => item.value).map(item => (
                        <div key={item.label} className="flex items-center gap-2">
                          <StatusDot ok={item.value} />
                          <span className="text-sm text-gray-700">{item.label}</span>
                        </div>
                      ))}
                    </div>
                    {e.regulatoryProfile?.dataTypes?.length > 0 && (
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">Sensitive Data Types</p>
                        <div className="flex flex-wrap gap-1">
                          {e.regulatoryProfile.dataTypes.map((dt, i) => <Badge key={i} color="purple">{dt}</Badge>)}
                        </div>
                      </div>
                    )}
                  </div>
                </Section>
              </div>

              {whois && !whois.error && (
                <Section title="Domain Registration" subtitle="WHOIS / RDAP data">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div><p className="text-xs text-gray-400">Registrar</p><p className="text-sm font-medium text-gray-900">{fmt(whois.registrar)}</p></div>
                    <div><p className="text-xs text-gray-400">Registered</p><p className="text-sm font-medium text-gray-900">{whois.registrationDate ? new Date(whois.registrationDate).toLocaleDateString() : 'Unknown'}</p></div>
                    <div><p className="text-xs text-gray-400">Expires</p><p className="text-sm font-medium text-gray-900">{whois.expirationDate ? new Date(whois.expirationDate).toLocaleDateString() : 'Unknown'}</p></div>
                    <div><p className="text-xs text-gray-400">Privacy Protected</p><p className="text-sm font-medium text-gray-900">{whois.privacyProtected ? 'Yes' : 'No'}</p></div>
                  </div>
                </Section>
              )}
            </div>
          )}

          {/* ── Security Posture Tab ── */}
          {activeTab === 'security' && (
            <div className="space-y-5">
              {/* Overall grade hero */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-5">
                  {e.securityPosture?.overallGrade && <Grade grade={e.securityPosture.overallGrade} size="lg" />}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">External Security Grade</p>
                    <p className="text-sm text-gray-600 mt-0.5">{e.securityPosture?.overallGradeRationale}</p>
                  </div>
                </div>
              </div>

              {/* Sub-grades */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <Section title="Email Security">
                  <div className="flex items-start gap-3 mb-3">
                    <Grade grade={e.securityPosture?.emailSecurity?.grade} size="sm" />
                    <p className="text-xs text-gray-500 pt-1">{e.securityPosture?.emailSecurity?.notes}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm"><span className="text-gray-500">SPF</span><Badge color={dns?.hasSPF ? 'green' : 'red'}>{dns?.hasSPF ? 'Present' : 'Missing'}</Badge></div>
                    <div className="flex items-center justify-between text-sm"><span className="text-gray-500">DMARC</span><Badge color={dns?.hasDMARC ? 'green' : 'red'}>{dns?.hasDMARC ? `${dns.dmarcPolicy || 'present'}` : 'Missing'}</Badge></div>
                    <div className="flex items-center justify-between text-sm"><span className="text-gray-500">DKIM</span><Badge color={dns?.hasDKIM ? 'green' : 'amber'}>{dns?.hasDKIM ? 'Present' : 'Not detected'}</Badge></div>
                  </div>
                </Section>

                <Section title="TLS / Certificate">
                  <div className="flex items-start gap-3 mb-3">
                    <Grade grade={(sslLabs?.status === 'ready' && sslLabs.grade) || e.securityPosture?.certificateSecurity?.grade} size="sm" />
                    <p className="text-xs text-gray-500 pt-1">{e.securityPosture?.certificateSecurity?.notes}</p>
                  </div>
                  <div className="space-y-2">
                    {ssl && !ssl.error && (
                      <>
                        <div className="flex justify-between text-sm"><span className="text-gray-500">Issuer</span><span className="font-medium text-gray-900 text-xs truncate max-w-[140px]">{ssl.issuer}</span></div>
                        <div className="flex justify-between text-sm"><span className="text-gray-500">Expires</span>
                          <Badge color={ssl.isExpired ? 'red' : ssl.isExpiringSoon ? 'amber' : 'green'}>
                            {ssl.isExpired ? 'Expired' : `${ssl.daysUntilExpiry}d`}
                          </Badge>
                        </div>
                      </>
                    )}
                    {sslLabs?.status === 'ready' && (
                      <>
                        <div className="flex justify-between text-sm"><span className="text-gray-500">HSTS</span><Badge color={sslLabs.hsts === 'present' ? 'green' : 'amber'}>{sslLabs.hsts || 'Missing'}</Badge></div>
                        <div className="flex justify-between text-sm"><span className="text-gray-500">OCSP Staple</span><Badge color={sslLabs.ocspStapling ? 'green' : 'gray'}>{sslLabs.ocspStapling ? 'On' : 'Off'}</Badge></div>
                        {sslLabs.findings?.length > 0 && (
                          <p className="text-[11px] text-amber-700 pt-1">
                            {sslLabs.findings.length} finding{sslLabs.findings.length > 1 ? 's' : ''}: {sslLabs.findings.slice(0, 2).map(f => f.issue).join('; ')}{sslLabs.findings.length > 2 ? '…' : ''}
                          </p>
                        )}
                        {sslLabs.reportUrl && (
                          <a href={sslLabs.reportUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-primary-600 hover:underline">View SSL Labs report →</a>
                        )}
                      </>
                    )}
                    {sslLabs?.status === 'pending' && (
                      <p className="text-[11px] text-gray-500 italic">SSL Labs scan queued — re-run to see grade</p>
                    )}
                  </div>
                </Section>

                <Section title="HTTP Headers">
                  <div className="flex items-start gap-3 mb-3">
                    <Grade grade={observatory?.status === 'ready' ? observatory.grade : null} size="sm" />
                    <p className="text-xs text-gray-500 pt-1">
                      {observatory?.status === 'ready' ? `${observatory.passedCount}/${observatory.totalCount} tests passed`
                        : observatory?.status === 'error' ? 'Mozilla Observatory unavailable'
                        : 'Not yet run'}
                    </p>
                  </div>
                  {observatory?.status === 'ready' && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm"><span className="text-gray-500">Score</span><span className="font-medium text-gray-900 text-xs tabular-nums">{observatory.score ?? '—'}</span></div>
                      {observatory.failedTests?.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-[11px] text-gray-400">Failed:</p>
                          <div className="flex flex-wrap gap-1">
                            {observatory.failedTests.slice(0, 4).map((t, i) => (
                              <Badge key={i} color="amber">{t.name.replace(/-/g, ' ')}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {observatory.reportUrl && (
                        <a href={observatory.reportUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-primary-600 hover:underline">View Observatory report →</a>
                      )}
                    </div>
                  )}
                </Section>

                <Section title="DNS Security">
                  <div className="flex items-start gap-3 mb-3">
                    <Grade grade={e.securityPosture?.dnsSecurity?.grade} size="sm" />
                    <p className="text-xs text-gray-500 pt-1">{e.securityPosture?.dnsSecurity?.notes}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-gray-500">Provider</span><span className="font-medium text-gray-900 text-xs">{dns?.dnsProvider}</span></div>
                    <div className="flex items-center justify-between text-sm"><span className="text-gray-500">CAA</span><Badge color={dns?.hasCAA ? 'green' : 'amber'}>{dns?.hasCAA ? 'Present' : 'Missing'}</Badge></div>
                  </div>
                </Section>
              </div>

              {/* Attack surface */}
              {e.securityPosture?.attackSurface && (
                <Section title="External Attack Surface" subtitle={`${result?.ctSubdomains?.length || ssl?.sans?.length || 0} subdomain${(result?.ctSubdomains?.length || ssl?.sans?.length || 0) !== 1 ? 's' : ''} discovered via certificate transparency and SSL`}>
                  {/* Notable subdomains from AI analysis */}
                  {e.securityPosture.attackSurface.notableSubdomains?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-400 mb-1.5">Notable Subdomains</p>
                      <div className="flex flex-wrap gap-1.5">
                        {e.securityPosture.attackSurface.notableSubdomains.map((s, i) => (
                          <Badge key={i} color={['vpn', 'mail', 'remote', 'owa', 'portal', 'api', 'admin', 'dev', 'staging', 'test'].some(k => s.toLowerCase().includes(k)) ? 'amber' : 'gray'}>{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Open ports from Shodan */}
                  {(e.securityPosture.attackSurface.openPorts?.length > 0 || result?.shodan?.ports?.length > 0) && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-400 mb-1.5">Open Ports {result?.shodan?.ip ? `(${result.shodan.ip})` : ''}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(result?.shodan?.ports || []).map((port, i) => (
                          <Badge key={i} color={[22, 23, 3389, 445, 139, 21].includes(port) ? 'red' : [80, 443].includes(port) ? 'green' : 'amber'}>{port}</Badge>
                        ))}
                      </div>
                      {e.securityPosture.attackSurface.openPorts?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {e.securityPosture.attackSurface.openPorts.map((note, i) => (
                            <p key={i} className="text-xs text-gray-600">{note}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Known vulnerabilities from Shodan */}
                  {(e.securityPosture.attackSurface.knownVulnerabilities?.length > 0 || result?.shodan?.vulns?.length > 0) && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-400 mb-1.5">Known Vulnerabilities</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(result?.shodan?.vulns || []).map((cve, i) => <Badge key={i} color="red">{cve}</Badge>)}
                      </div>
                    </div>
                  )}

                  {e.securityPosture.attackSurface.notes && <p className="text-xs text-gray-500 mt-2">{e.securityPosture.attackSurface.notes}</p>}

                  {/* Full subdomain list (collapsible) */}
                  {result?.ctSubdomains?.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                        View all {result.ctSubdomains.length} discovered subdomains
                      </summary>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {result.ctSubdomains.map((s, i) => (
                          <Badge key={i} color={['vpn', 'mail', 'remote', 'owa', 'portal', 'api', 'admin', 'dev', 'staging', 'test'].some(k => s.includes(k)) ? 'amber' : 'gray'}>{s}</Badge>
                        ))}
                      </div>
                    </details>
                  )}
                </Section>
              )}

              {/* Observations + Quick Wins */}
              <Section title="Security Observations">
                <div className="space-y-3">
                  {(e.securityPosture?.initialObservations || []).map((obs, i) => {
                    const text = typeof obs === 'string' ? obs : obs.observation
                    const why = typeof obs === 'string' ? null : obs.whyItMatters
                    const wtd = typeof obs === 'string' ? null : obs.whatToDo
                    return (
                      <div key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5" />
                        <div>
                          <p className="text-sm text-gray-700">{text}</p>
                          {why && <p className="text-xs text-gray-500 mt-0.5 italic">Why it matters: {why}</p>}
                          {wtd && <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">What to do: {wtd}</p>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Section>

              <Section title="Quick Wins">
                <div className="space-y-3">
                  {(e.securityPosture?.quickWins || []).map((qw, i) => {
                    const text = typeof qw === 'string' ? qw : qw.action
                    const wtd = typeof qw === 'string' ? null : qw.whatToDo
                    const loe = typeof qw === 'string' ? null : qw.levelOfEffort
                    const loeColor = loe?.toLowerCase().startsWith('low') ? 'green' : loe?.toLowerCase().startsWith('med') ? 'amber' : loe?.toLowerCase().startsWith('high') ? 'red' : 'gray'
                    const loeColors = { green: 'bg-green-100 text-green-700', amber: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700', gray: 'bg-gray-100 text-gray-600' }
                    return (
                      <div key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-gray-700">{text}</p>
                            {loe && <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${loeColors[loeColor]}`}>Effort: {loe}</span>}
                          </div>
                          {wtd && <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">What to do: {wtd}</p>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Section>

              <Section title="Suggested Critical Systems">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {(e.securityPosture?.suggestedCriticalSystems || []).map((sys, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-gray-50">
                      <span className="text-xs text-gray-400 w-4">{i + 1}.</span>
                      <span className="text-gray-700">{sys}</span>
                    </div>
                  ))}
                </div>
              </Section>
            </div>
          )}

          {/* ── Engagement Intel Tab ── */}
          {activeTab === 'engagement' && (
            <div className="space-y-5">
              {/* Maturity estimate */}
              {e.engagementContext?.maturityEstimate && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Estimated Maturity Level</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{e.engagementContext.maturityEstimate}</p>
                      <p className="text-xs text-gray-500 mt-1">{e.engagementContext.maturityRationale}</p>
                    </div>
                    {e.engagementContext?.threatProfileRecommendation && (
                      <div className="text-right max-w-[280px]">
                        <p className="text-xs text-gray-400 mb-1">Recommended Threat Profile</p>
                        <p className="text-sm font-medium text-gray-700">{e.engagementContext.threatProfileRecommendation}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Section title="Key Risks">
                  <div className="space-y-2">
                    {(e.engagementContext?.keyRisks || []).map((risk, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5" />
                        <p className="text-sm text-gray-700">{risk}</p>
                      </div>
                    ))}
                  </div>
                </Section>

                <Section title="Suggested Focus Areas">
                  <div className="space-y-2">
                    {(e.engagementContext?.suggestedFocusAreas || []).map((area, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5" />
                        <p className="text-sm text-gray-700">{area}</p>
                      </div>
                    ))}
                  </div>
                </Section>
              </div>

              {e.engagementContext?.conversationStarters?.length > 0 && (
                <Section title="First Meeting Conversation Starters">
                  <div className="space-y-2">
                    {e.engagementContext.conversationStarters.map((q, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#012438] text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
                        <p className="text-sm text-gray-700">{q}</p>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          )}

          {/* Confidence footer */}
          {e.confidence && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mt-5">
              <Badge color={e.confidence.overall === 'high' ? 'green' : e.confidence.overall === 'medium' ? 'amber' : 'red'}>{e.confidence.overall} confidence</Badge>
              <span className="text-xs text-gray-500">{e.confidence.notes}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
